import { ChevronDownIcon } from "@heroicons/react/20/solid";
import PrivacyMode from "components/Text/PrivacyMode";
import type { Company } from "kubb";
import { lowerCase } from "lodash-es";
import type React from "react";
import { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CompanyContext } from "reducers/company";
import VirtualizedDropdownList from "../../components/Theme/VirtualizedDropdownList";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
  DropdownSearchInput,
} from "../../components/Theme/dropdown";
import { useDropdownSearch } from "../../components/Theme/useDropdownSearch";
import { getUrlHash } from "../../components/navigation/URL";
import DisplayIfUserType from "../../components/utils/DisplayIfUserType/DisplayIfUserType";
import { isUserPartnerAdmin, isUserSuperAdmin } from "../../model/user";
import { usePrivacyMode } from "../../reducers/privacy";
import { useProfile } from "../../reducers/profile";

// Hoisted so `useDropdownSearch`'s useMemo(filteredItems) sees a stable
// reference and doesn't recompute the filter on every parent render.
const selectCompanyName = (c: Company): string => c.name;

export default function CompanySelect(): React.ReactElement {
  const { state } = useContext(CompanyContext);

  const privacy = usePrivacyMode();
  const { companies = [], selected } = state;
  const user = useProfile();

  const navigate = useNavigate();

  const sortedCompanies = useMemo(
    () =>
      [...companies].sort((c1, c2) =>
        lowerCase(c1.name).localeCompare(lowerCase(c2.name)),
      ),
    [companies],
  );

  const {
    searchQuery,
    setSearchQuery,
    filteredItems: filteredCompanies,
    ResetSearchOnMount,
  } = useDropdownSearch(sortedCompanies, selectCompanyName);

  const setCompany = (company?: Company | "ALL"): void => {
    if (company !== "ALL") {
      if (
        state.selected === "ALL" ||
        state.selected?.company_id !== company?.company_id
      ) {
        navigate({
          hash: getUrlHash(company, undefined, privacy),
        });
      }
    } else {
      if (state.selected !== "ALL") {
        navigate({
          hash: getUrlHash(company, undefined, privacy),
        });
      }
    }
  };

  return (
    <>
      {(companies.length > 1 ||
        isUserSuperAdmin(user) ||
        isUserPartnerAdmin(user)) && (
        <Dropdown>
          <DropdownButton outline data-cy={"company-select"}>
            <span className={"hidden md:inline-block"}>Company: </span>
            <span
              className={
                "text-grey w-auto md:w-32 text-left overflow-clip text-xs md:text-sm text-nowrap"
              }
              data-cy={"company-select-selected"}
            >
              {selected ? (
                <PrivacyMode>
                  {selected === "ALL" ? "ALL" : selected.name}
                </PrivacyMode>
              ) : (
                "None"
              )}
            </span>

            <ChevronDownIcon />
          </DropdownButton>
          <DropdownMenu data-cy={"company-select-menu"}>
            <ResetSearchOnMount />
            <DropdownSearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search companies..."
              aria-label="Search companies"
            />
            <DisplayIfUserType userTypes={"superadmin"}>
              <DropdownItem key={"ALL"} onClick={() => setCompany("ALL")}>
                ALL
              </DropdownItem>
              <DropdownItem key={"NONE"} onClick={() => setCompany(undefined)}>
                NONE
              </DropdownItem>
            </DisplayIfUserType>
            <VirtualizedDropdownList<Company>
              items={filteredCompanies}
              getItemKey={(company) => company.company_id}
              renderLabel={(company) => (
                <PrivacyMode>{company.name}</PrivacyMode>
              )}
              onSelect={setCompany}
              isSelected={(company) =>
                selected !== undefined &&
                selected !== "ALL" &&
                company.company_id === selected.company_id
              }
              itemTestId={() => "company-select-item"}
              emptyMessage={searchQuery ? "No results" : "No companies"}
              aria-label="Companies"
            />
          </DropdownMenu>
        </Dropdown>
      )}
    </>
  );
}
